import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { httpBatchLink } from '@trpc/client';
import { trpc } from './trpc';
import { Toaster } from '@/components/ui/sonner';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import {
  firebaseConfig,
  baseApiUrl,
  VITE_ENV,
  FIREBASE_AUTH_EMULATOR_HOST,
  isLocal,
} from './config';
import { getApps, initializeApp } from 'firebase/app';
import { UserContextProvider } from './context/UserContext';
import { Router } from './Router';
import { useState } from 'react';
import { GuaranteeFeatures } from './GuaranteeFeatures';
import { getStoredOrganizationId, isValidObjectId } from './utils/organization-storage';
import { ErrorBoundary } from 'react-error-boundary';
import { AppError } from './AppError';
import { SentryMonitoringService } from '@libs/frontend-services';

if (!getApps().length) {
  const app = initializeApp(firebaseConfig);
  const sentryMonitoringService = new SentryMonitoringService();
  // Use dynamic import to avoid content blocker issues with firebase/analytics import
  if (VITE_ENV !== 'local') {
    import('firebase/analytics')
      .then(({ initializeAnalytics }) => {
        initializeAnalytics(app);
      })
      .catch((error) => {
        // Firebase Analytics may be blocked by content blockers
        // This is safe to ignore in development
        sentryMonitoringService.captureMessage('Error initializing Firebase Analytics', 'error', {
          error,
        });
      });
  }
}

export const App = () => {
  const [auth] = useState(getAuth());
  if (FIREBASE_AUTH_EMULATOR_HOST && !auth.settings.appVerificationDisabledForTesting) {
    connectAuthEmulator(auth, FIREBASE_AUTH_EMULATOR_HOST);
    auth.settings.appVerificationDisabledForTesting = true;
  }

  // Disable reCAPTCHA in preview environments for automated QA testing
  if (import.meta.env.VITE_DISABLE_RECAPTCHA === 'true' && !auth.settings.appVerificationDisabledForTesting) {
    auth.settings.appVerificationDisabledForTesting = true;
  }
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 5,
        retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 30000),
        refetchOnWindowFocus: false,
      },
    },
  });
  const trpcClient = trpc.createClient({
    links: [
      httpBatchLink({
        url: `${baseApiUrl}/trpc`,
        async headers() {
          // Read fresh from localStorage on EVERY request - no closure variables
          const organizationId = getStoredOrganizationId();
          const headers: Record<string, string> = {};

          try {
            // Get a fresh token each time
            const jwt = await getAuth().currentUser?.getIdToken();

            if (jwt) {
              headers.Authorization = jwt;

              // Only include Organization header if it's a valid ObjectId
              if (organizationId && isValidObjectId(organizationId)) {
                headers['x-organization-id'] = organizationId;
              }
            }
            // eslint-disable-next-line no-empty
          } catch {}

          return headers;
        },
      }),
    ],
  });

  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    const sentryMonitoringService = new SentryMonitoringService();

    // Log to Sentry with context
    sentryMonitoringService.captureException(error, {
      tags: {
        errorBoundaryLevel: 'app',
        errorType: 'react-component-error',
      },
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      extra: {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      },
    });

    // Log to console in development
    if (isLocal) {
      // eslint-disable-next-line no-console
      console.error('App-level error boundary caught an error:', error, errorInfo);
    }
  };

  return (
    <ErrorBoundary FallbackComponent={AppError} onError={handleError}>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <UserContextProvider>
            <GuaranteeFeatures>
              <Router />
              <Toaster expand={true} />
            </GuaranteeFeatures>
          </UserContextProvider>
        </QueryClientProvider>
      </trpc.Provider>
    </ErrorBoundary>
  );
};
